import { ProviderType } from "@ternary/api-lib/analytics/enums";
import { billingCucSchema } from "@ternary/api-lib/analytics/schemas/billingCUC";
import { billingFocusSchema } from "@ternary/api-lib/analytics/schemas/billingFOCUS";
import { Dimension } from "@ternary/api-lib/analytics/ui/types";

/**
 * Resolves a dimension display name from various sources in the FOCUS schema.
 *
 * This function attempts to find a display name for a given dimension key through:
 * 1. Direct key match in the FOCUS dimensions
 * 2. Custom label match by display name (only for TERNARY providerType)
 * 3. Billing dimension match (maps from billingCUC schema to billingFOCUS schema)
 * 4. Returns the original key if no match is found
 *
 * @param existingDimensionKey - The dimension key to resolve (can be a schema name or display name)
 * @param availableDimensionsKeyedBySchemaName - Record of available dimensions keyed by schema name
 * @returns The resolved display name for the dimension, or the original key if no match is found
 */
export function focusDimensionDisplayNameConverter(
  existingDimensionKey: string,
  availableDimensionsKeyedBySchemaName: Record<string, Dimension>
): string {
  // 1. Direct key match (FOCUS dimensions)
  const direct = availableDimensionsKeyedBySchemaName[existingDimensionKey];
  if (direct) {
    return direct.displayName;
  }

  // 2. Custom label match (by displayName)
  const customLabel = Object.values(availableDimensionsKeyedBySchemaName).find(
    (dimension) => dimension.displayName === existingDimensionKey
  );
  if (customLabel && customLabel.providerType === ProviderType.TERNARY) {
    return customLabel.displayName;
  }

  // 3. Billing dimension match (schemaName in billingCUC to schemaName in billingFOCUS)
  const billingDimension: Dimension | undefined = Object.values(
    billingCucSchema.dimensions
  ).find((dimension) => dimension.schemaName === existingDimensionKey);

  if (billingDimension && billingDimension.displayName) {
    const focusMatch = Object.values(billingFocusSchema.dimensions).find(
      (dimension) => dimension.displayName === billingDimension.displayName
    );
    if (focusMatch) {
      return focusMatch.displayName;
    }
  }
  // 4. Fallback
  return existingDimensionKey;
}
